import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, uploadLessonCover, getUserAssignedCourses, type AssignedCourse } from '../lib/api';
import { useCourseUIStore } from '../store/useCourseUIStore';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, RefreshCw, Loader2, FileText } from 'lucide-react';

// Import Components
import { CourseSidebar } from '../components/course-detail/CourseSidebar';
import { LessonMetaEditor } from '../components/course-detail/LessonMetaEditor';
import { ArtifactTabs } from '../components/course-detail/ArtifactTabs';
import LogoutButton from '../components/common/LogoutButton';
import TokenBalance from '../components/TokenBalance';
import StudentPortalButton from '../components/StudentPortalButton';
import logo from '../assets/imgs/logo.png';

// --- Types ---
type Lesson = {
  id: number;
  title: string;
  learning_objectives: string;
  prerequisites: string;
  sort_order: number;
  cover_image_url?: string;
};

type Stage = {
  id: number;
  title: string;
  sort_order: number;
  lessons?: Lesson[];
  isLoading?: boolean;
};

export default function CourseDetailPage() {
  const { courseId } = useParams();
  const cId = parseInt(courseId || '0');
  const navigate = useNavigate();
  const store = useCourseUIStore();
  const { user } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [stages, setStages] = useState<Stage[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [courseName, setCourseName] = useState<string>('');
  const [editForm, setEditForm] = useState({
    title: '', learning_objectives: '{}'
  });
  const [pendingInsertAfter, setPendingInsertAfter] = useState<number | null>(null);
  const [plans, setPlans] = useState([]);
  const [slides, setSlides] = useState([]);
  const [assignedCourseData, setAssignedCourseData] = useState<AssignedCourse | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(() => {
    const savedPlanId = localStorage.getItem('globalSelectedPlanId');
    return savedPlanId ? parseInt(savedPlanId) : null;
  });

  // Modal State (Managed here, Rendered in LessonMetaEditor)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [planStatus, setPlanStatus] = useState<any>(null);

  // 全局加载状态，用于所有API请求的加载提示
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  // 请求计数器，用于跟踪当前活跃的API请求数量
  const [requestCount, setRequestCount] = useState(0);

  // 开始API请求，增加请求计数
  const startRequest = () => {
    setRequestCount(prev => {
      const newCount = prev + 1;
      if (newCount === 1) {
        setIsGlobalLoading(true);
      }
      return newCount;
    });
  };

  // 结束API请求，减少请求计数
  const endRequest = () => {
    setRequestCount(prev => {
      const newCount = prev - 1;
      if (newCount === 0) {
        setIsGlobalLoading(false);
      }
      return newCount;
    });
  };

  // 包装API请求，自动处理加载状态
  const wrapApiRequest = async (apiCall: () => Promise<any>) => {
    startRequest();
    try {
      const result = await apiCall();
      return result;
    } finally {
      endRequest();
    }
  };

  // 1. Initialize
  useEffect(() => {
    console.log('=== CourseDetailPage 初始化 ===');
    console.log('课程ID:', cId);
    console.log('当前用户:', user);
    
    if (!cId) {
      console.log('课程ID不存在，返回');
      return;
    }
    
    console.log('初始化课程状态...');
    store.initCourse(cId);

    // 当用户变化时，强制重新加载数据，不使用缓存
    if (user) {
      console.log('用户存在，调用fetchStages获取数据');
      fetchStages();
    }
  }, [cId, user]);

  // 2. Auto Restore
  useEffect(() => {
    console.log('=== CourseDetailPage 自动恢复状态 ===');
    console.log('选中的课时ID:', store.selectedLessonId);
    
    if (store.selectedLessonId) {
      console.log('开始查找选中的课时...');
      let foundLesson: Lesson | undefined;
      for (const s of stages) {
        if (s.lessons) {
          const found = s.lessons.find(l => l.id === store.selectedLessonId);
          if (found) {
            foundLesson = found;
            console.log('找到选中的课时:', foundLesson.title);
            break;
          }
        }
      }

      if (foundLesson) {
        if (!selectedLesson || selectedLesson.id !== foundLesson.id) {
          console.log('切换到新课时，清空之前的状态');
          // 切换lesson时，先清空之前的查找结果
          setPlans([]);
          setSlides([]);
          // 切换lesson时，清空之前的教案plan id的持久化存储，因为不同lesson的教案版本是独立的
          setSelectedLesson(foundLesson);
          setEditForm({ 
            title: foundLesson.title, 
            learning_objectives: foundLesson.learning_objectives || '{}' 
          });
          // 传递保存的selectedPlanId，确保使用正确的教案版本
          // 注意：selectedPlanId的初始状态是从localStorage恢复的，所以会使用之前保存的教案版本
          console.log('获取新课时的artifacts');
          fetchArtifacts(foundLesson.id, null, false); // 新课时，使用null作为初始planId
        }
      } else {
        console.log('未找到选中的课时，清空状态');
        // 没有选中lesson时，清空所有相关状态
        setSelectedLesson(null);
        setPlans([]);
        setSlides([]);
        // 清空教案plan id的长久存储
        localStorage.removeItem('globalSelectedPlanId');
        setSelectedPlanId(null);
      }
    }
  }, [stages, store.selectedLessonId, selectedLesson]); // 添加selectedLesson作为依赖，避免无限循环

  // --- API Handlers ---

  const fetchStages = async () => {
    setLoading(true);
    try {
      console.log('=== CourseDetailPage fetchStages ===');
      console.log('当前用户:', user);
      console.log('当前课程ID:', cId);
      
      // 获取课程详情，包括课程名称
      const courseRes = await wrapApiRequest(() => api.get(`/courses/${cId}/`));
      setCourseName(courseRes.data.title);
      
      if (user?.role === 'sys') {
        console.log('sys角色，获取所有阶段');
        const res = await wrapApiRequest(() => api.get(`/courses/${cId}/stages/`));
        store.setCourseData(cId, res.data);
        setStages(res.data);
        if (store.expandedStageIds.length === 0 && res.data.length > 0) {
          const firstId = res.data[0].id;
          store.setExpandedStages([firstId]);
          loadStageLessons(firstId, res.data);
        } else {
          store.expandedStageIds.forEach(sid => loadStageLessons(sid, res.data));
        }
      } else {
        console.log('非sys角色，获取用户权限数据');
        // 强制重新获取用户权限数据，不使用缓存
        const res = await getUserAssignedCourses(user!.id);
        console.log('用户权限数据:', res.data);
        
        const assigned = res.data.find(ac => ac.course_id === cId);
        console.log('当前课程权限数据:', assigned);
        
        if (!assigned) {
          console.log('未找到当前课程权限，重定向到/dashboard');
          navigate('/dashboard');
          return;
        }
        
        // 检查用户是否对当前课程有edit_develop权限
        const hasEditPermission = assigned.stages?.some(stage => {
          return stage.lessons?.some(lesson => {
            return lesson.permission_type === 'edit_develop';
          });
        });
        
        console.log('用户是否对当前课程有edit_develop权限:', hasEditPermission);
        
        if (!hasEditPermission && user?.role !== 'sys') {
          console.log('无权限访问该课程，重定向到/dashboard');
          navigate('/dashboard');
          return;
        }
        
        setAssignedCourseData(assigned);
        
        const permissionStageIds = new Set(assigned.stages?.map(s => s.stage_id) || []);
        console.log('有权限的阶段IDs:', permissionStageIds);
        
        const allStagesRes = await wrapApiRequest(() => api.get(`/courses/${cId}/stages/`));
        console.log('所有阶段数据:', allStagesRes.data);
        
        const filteredStages = allStagesRes.data.filter((stage: Stage) => permissionStageIds.has(stage.id));
        console.log('过滤后的阶段:', filteredStages);
        
        store.setCourseData(cId, filteredStages);
        setStages(filteredStages);
        
        if (filteredStages.length > 0) {
          const firstId = filteredStages[0].id;
          if (store.expandedStageIds.length === 0) {
            store.setExpandedStages([firstId]);
          }
          store.expandedStageIds.forEach(sid => {
            if (permissionStageIds.has(sid)) {
              loadStageLessonsWithPermission(sid, filteredStages, assigned);
            }
          });
        }
      }
    } catch (err) { 
      console.error('fetchStages错误:', err);
      navigate('/dashboard');
    }
    finally { setLoading(false); }
  };

  const loadStageLessonsWithPermission = async (stageId: number, currentStages: Stage[], assignedData: AssignedCourse) => {
    try {
      setStages(prev => prev.map(s => s.id === stageId ? { ...s, isLoading: true } : s));
      
      const stageData = assignedData.stages?.find(s => s.stage_id === stageId);
      const permissionLessonIds = new Set(stageData?.lessons?.map(l => l.lesson_id) || []);
      
      const res = await wrapApiRequest(() => api.get(`/courses/stages/${stageId}/lessons/`));
      const filteredLessons = res.data.filter((lesson: Lesson) => permissionLessonIds.has(lesson.id));
      
      store.updateStageLessons(cId, stageId, filteredLessons);
      setStages(prev => prev.map(s => s.id === stageId ? { ...s, lessons: filteredLessons, isLoading: false } : s));
    } catch (err) { 
      console.error(err);
      setStages(prev => prev.map(s => s.id === stageId ? { ...s, isLoading: false } : s));
    }
  };

  const loadStageLessons = async (stageId: number, currentStages: Stage[]) => {
    try {
      setStages(prev => prev.map(s => s.id === stageId ? { ...s, isLoading: true } : s));
      const res = await wrapApiRequest(() => api.get(`/courses/stages/${stageId}/lessons/`));
      
      if (user?.role === 'sys') {
        store.updateStageLessons(cId, stageId, res.data);
        setStages(prev => prev.map(s => s.id === stageId ? { ...s, lessons: res.data, isLoading: false } : s));
      } else if (assignedCourseData) {
        const stageData = assignedCourseData.stages?.find(s => s.stage_id === stageId);
        const permissionLessonIds = new Set(stageData?.lessons?.map(l => l.lesson_id) || []);
        const filteredLessons = res.data.filter((lesson: Lesson) => permissionLessonIds.has(lesson.id));
        
        store.updateStageLessons(cId, stageId, filteredLessons);
        setStages(prev => prev.map(s => s.id === stageId ? { ...s, lessons: filteredLessons, isLoading: false } : s));
      }
    } catch (err) { 
      console.error(err);
      setStages(prev => prev.map(s => s.id === stageId ? { ...s, isLoading: false } : s));
    }
  };

  const fetchArtifacts = async (lessonId: number, specificPlanId?: number | null, saveToLocalStorage: boolean = false, forceRefresh: boolean = false) => {
    if (lessonId > 1000000000000) {
      setPlans([]);
      setSlides([]);
      return;
    }
    try {
      // 1. 优化：只在特定情况下请求plans接口
      // - 当specificPlanId变化时
      // - 当lessonId变化时
      // - 当没有plans数据时
      // - 当forceRefresh为true时
      const shouldFetchPlans = forceRefresh || !plans.length || selectedLesson?.id !== lessonId || (specificPlanId && !plans.some((p: any) => p.id === specificPlanId));
      
      let pData = plans;
      if (shouldFetchPlans) {
        const p = await wrapApiRequest(() => api.get(`/courses/lessons/${lessonId}/plans/`));
        pData = p.data;
        setPlans(pData);
      }

      // 确定要使用的planId：
      // 1. 如果传入了specificPlanId，并且它存在于plans数据中，就使用它
      // 2. 否则，使用第一个计划的id
      const planIds = pData.map((plan: any) => plan.id);
      const planId = specificPlanId && planIds.includes(specificPlanId) ? specificPlanId : (pData[0] as any)?.id;

      // 2. 优化：只在planId变化时请求slides接口
      if (planId) {
        // 只有当planId变化或没有slides数据或强制刷新时才请求
        if (planId !== selectedPlanId || !slides.length || forceRefresh) {
          const s = await wrapApiRequest(() => api.get(`/slides/plans/${planId}/slides/`));
          setSlides(s.data);
        }
      } else {
        setSlides([]);
      }

      // 3. 优化：只在planId实际变化时更新状态
      if (planId && planId !== selectedPlanId) {
        setSelectedPlanId(planId); // 更新选中的planId
        // 保存到localStorage，实现持久化 - 只有当saveToLocalStorage为true时才保存
        if (saveToLocalStorage) {
          localStorage.setItem('globalSelectedPlanId', planId.toString());
        }
      } else if (!planId && selectedPlanId !== null) {
        setSelectedPlanId(null);
      }
    } catch (e) {
      console.error('获取 artifacts 失败:', e);
    }
  };

  // --- Interaction Handlers ---

  const handleLessonSelect = (lesson: Lesson) => store.setSelectedLessonId(lesson.id);

  // 处理幻灯片状态变化，刷新幻灯片列表
  const handleSlideStatusChange = () => {
    if (selectedLesson && selectedPlanId) {
      fetchArtifacts(selectedLesson.id, selectedPlanId, false, true);
    }
  };

  const handleEnterWorkshop = async (mode: 'plan' | 'ware', artifactId?: number) => {
    if (!selectedLesson) return;
    if (selectedLesson.id > 1000000000000) return alert("Please save the new lesson first.");

    const parentStage = stages.find(s => s.lessons?.some(l => l.id === selectedLesson.id));
    if (!parentStage) return alert("Stage info missing.");

    // ✅ 定义 state 对象
    const navState = { state: { backCourseId: courseId } };

    if (mode === 'ware') {
      try {
        const res = await wrapApiRequest(() => api.get(`/courses/lessons/${selectedLesson.id}/artifacts/`));
        const targetId = artifactId || res.data.latest_slide_id;

        if (targetId) {
          // ✅ 传递 state
          navigate(`/workshop/ware/${parentStage.id}/${selectedLesson.id}/${targetId}`, navState);
        } else {
          alert("No slides found.");
        }
      } catch (e) { alert("Error fetching slides"); }
      return;
    }

    try {
      if (artifactId) {
        // ✅ 传递 state
        navigate(`/workshop/plan/${parentStage.id}/${selectedLesson.id}/${artifactId}`, navState);
        return;
      }
      const statusRes = await wrapApiRequest(() => api.get(`/courses/lessons/${selectedLesson.id}/plan-status/`));
      if (statusRes.data.active_draft_id) {
        // ✅ 传递 state
        navigate(`/workshop/plan/${parentStage.id}/${selectedLesson.id}/${statusRes.data.active_draft_id}`, navState);
      } else {
        setPlanStatus(statusRes.data);
        setIsModalOpen(true);
      }
    } catch (e) { alert("Failed to check plan status."); }
  };


  // 🔥 Create Draft Handler (Create Only, No Redirect, With Feedback)
  const handleCreateDraft = async (baseArtifactId: number | null) => {
    // 1. Pre-flight Checks
    if (!selectedLesson) {
      console.warn("Operation Aborted: No lesson selected.");
      return;
    }

    const parentStage = stages.find(s => s.lessons?.some(l => l.id === selectedLesson.id));
    if (!parentStage) {
      alert("System Error: Stage info missing. Please refresh.");
      return;
    }

    try {
      // 2. API Call: Create new draft
      // console.log(`[Draft] Creating new plan for Lesson ID: ${selectedLesson.id}`);

      const res = await wrapApiRequest(() => api.post(`/courses/lessons/${selectedLesson.id}/plans/draft/`, {
        base_artifact_id: baseArtifactId // null = blank draft
      }));

      // console.log("[Draft] Created Successfully:", res.data);

      // 3. UI Update: Refresh artifact lists (Plans/Slides)
      await fetchArtifacts(selectedLesson.id, res.data.id, true);

      // 4. Success Feedback
      alert(`✅ New Plan Created Successfully! \nVersion: ${res.data.version_number}`);

    } catch (e) {
      console.error("[Draft] Creation Failed:", e);
      alert("❌ Failed to create new plan draft. Please check network or console logs.");
    }
  };


  // 🔥 Delete Plan Handler
  const handleDeletePlan = async (planId: number) => {
    if (!confirm("Are you sure you want to delete this draft plan?")) return;
    try {
      await wrapApiRequest(() => api.delete(`/courses/lessons/plans/${planId}/`)); // Ensure this endpoint exists in backend
      if (selectedLesson) {
        fetchArtifacts(selectedLesson.id, null, false, true);
      }
    } catch (e) {
      alert("Delete failed.");
    }
  };

  // 🔥 Delete Slide Version Handler
  const handleDeleteSlideVersion = async (slideId: number, versionId: number) => {
    if (!confirm(`Are you sure you want to delete this slide version?\nVersion ID: ${versionId}`)) return;
    try {
      // console.log('=== 删除slide version ===');
      // console.log('传递的参数:');
      // console.log('slideId:', slideId);
      // console.log('versionId:', versionId);

      // 问题所在：服务端期望的是versionId，但当前传递的是slideId
      // 应该使用versionId作为服务端API的slide_id参数
      await wrapApiRequest(() => api.delete(`/slides/${versionId}/`)); // 使用versionId作为slide_id参数

      if (selectedLesson) fetchArtifacts(selectedLesson.id, selectedPlanId, false, true); // 强制刷新
      // console.log('删除成功，重新获取artifacts');
    } catch (e: any) {
      // 显示更详细的错误信息
      const errorMsg = e.response?.data?.detail || e.message || "Delete slide version failed.";
      alert(`Delete failed: ${errorMsg}`);
      console.error('Delete slide version error:', e);
    }
  };

  // 🔥 Handle Plan Version Change
  const handlePlanVersionChange = async (planId: number) => {
    console.log('切换教案版本到:', planId);
    if (selectedLesson) {
      // 用户主动切换planId时，保存到localStorage
      await fetchArtifacts(selectedLesson.id, planId, true);
    }
  };

  const handleQuickInsert = async (stageId: number, afterLessonId: number, currentSortOrder: number) => {
    const tempId = Date.now();
    setPendingInsertAfter(afterLessonId);
    const newLesson: Lesson = { id: tempId, title: "New Lesson", learning_objectives: "{}", prerequisites: "", sort_order: currentSortOrder + 1 };

    setStages(prev => prev.map(stage => {
      if (stage.id !== stageId) return stage;
      const newLessons = [...(stage.lessons || [])];
      const idx = newLessons.findIndex(l => l.id === afterLessonId);
      newLessons.splice(idx + 1, 0, newLesson);
      for (let i = idx + 2; i < newLessons.length; i++) newLessons[i].sort_order += 1;
      return { ...stage, lessons: newLessons };
    }));
    store.setSelectedLessonId(tempId);
    setEditForm({ title: "New Lesson", learning_objectives: "{}" });
  };

  const handleDeleteLesson = async (stageId: number, lessonId: number, currentOrder: number) => {
    if (!confirm("Are you sure?")) return;
    setStages(prev => prev.map(s => s.id !== stageId ? s : { ...s, lessons: (s.lessons || []).filter(l => l.id !== lessonId) }));
    if (store.selectedLessonId === lessonId) { store.setSelectedLessonId(null); setSelectedLesson(null); }
    try { if (lessonId <= 1000000000000) await wrapApiRequest(() => api.delete(`/courses/lessons/${lessonId}/`)); }
    catch (e) { alert("Delete failed"); fetchStages(); }
  };

  const handleSaveLesson = async () => {
    if (!selectedLesson) return;
    const { title, learning_objectives } = editForm;

    try {
      if (selectedLesson.id > 1000000000000) {
        const stage = stages.find(s => s.lessons?.some(l => l.id === selectedLesson.id));
        const res = await wrapApiRequest(() => api.post('/courses/lessons/', { stage_id: stage?.id, title, learning_objectives, insert_after_id: pendingInsertAfter }));
        alert('✅ Created!');
        fetchStages();
        store.setSelectedLessonId(res.data.id);
      } else {
        await wrapApiRequest(() => api.put(`/courses/lessons/${selectedLesson.id}/`, { title, learning_objectives }));
        alert('✅ Saved!');
        store.updateLesson(cId, selectedLesson.id, { title });
        setStages(prev => prev.map(s => ({ ...s, lessons: s.lessons?.map(l => l.id === selectedLesson.id ? { ...l, title } : l) })));
      }
    } catch (err) { alert('❌ Save Failed'); }
  };

  // const handleUploadCover = async (file: File) => {
  //   if (!selectedLesson || selectedLesson.id > 1000000000000) return alert("Save lesson first.");
  //   try {
  //     // 包装uploadLessonCover函数，因为它内部也调用了api.post
  //     const res = await wrapApiRequest(() => uploadLessonCover(selectedLesson.id, file));
  //     store.updateLesson(cId, selectedLesson.id, { cover_image_url: res.data.cover_image_url });
  //     setStages(prev => prev.map(s => ({ ...s, lessons: s.lessons?.map(l => l.id === selectedLesson.id ? { ...l, cover_image_url: res.data.cover_image_url } : l) })));
  //     alert('✅ Uploaded!');
  //   } catch (err) { alert('❌ Upload Failed'); }
  // };

  // 处理课程名称换行的函数
  const renderCourseName = () => {
    if (!courseName) return null;
    
    // 替换冒号和连字符为带换行的版本
    const formattedName = courseName
      .replace(/:/g, ':\n')
      .replace(/-/g, '-\n');
    
    return formattedName.split('\n').map((line, index) => (
      <span key={index} className="block">{line}</span>
    ));
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden relative">
      {/* 全屏加载状态提示 - 浅色半透明背景 */}
      {isGlobalLoading && (
        <div className="fixed inset-0 z-50 bg-white/70 backdrop-blur-sm flex items-center justify-center">
          <div className="flex items-center gap-2 text-gray-600">
            <Loader2 className="animate-spin mr-2" />
            <span>加载中...</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="h-screen flex items-center justify-center text-gray-500">
          <Loader2 className="animate-spin mr-2" />
          Loading...
        </div>
      ) : (
        <>
          <header className="h-auto min-h-14 bg-white border-b px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-gray-500 hover:text-brand-600"><ArrowLeft size={20} /></Link>
              <div className="text-left">
                <h1 className="font-bold text-lg text-gray-800">{renderCourseName()}</h1>
              </div>
            </div>
            <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center h-14">
              <img src={logo} alt="Logo" className="h-full object-contain" />
            </div>
            <div className="flex items-center gap-4">
              <button onClick={fetchStages} className="text-gray-400 hover:text-brand-600 transition-colors" title="Force Refresh"><RefreshCw size={16} /></button>
              <TokenBalance />
              <LogoutButton />
            </div>
          </header>

          <div className="flex-1 flex overflow-hidden">
            {/* Left: Sidebar Component */}
            <CourseSidebar
              stages={stages}
              expandedStageIds={store.expandedStageIds}
              selectedLessonId={store.selectedLessonId}
              onToggleStage={(id) => { store.toggleStage(id); if (!store.expandedStageIds.includes(id)) loadStageLessons(id, stages); }}
              onSelectLesson={handleLessonSelect}
              onQuickInsert={handleQuickInsert}
              onDeleteLesson={handleDeleteLesson}
            />

            {/* Right: Content Area */}
            <div className="flex-1 overflow-y-auto bg-gray-50 p-8">
              {selectedLesson ? (
                <div className="max-w-4xl mx-auto space-y-8">
                  {/* Component: Meta Editor (With Modal) */}
                  <LessonMetaEditor
                    lessonId={selectedLesson.id}
                    courseId={cId}
                    form={editForm}
                    setForm={setEditForm}
                    onSave={handleSaveLesson}
                    // 🔥 New: Directly call create with null (blank draft)
                    onCreateNewPlan={() => handleCreateDraft(null)}
                  />

                  {/* Component: Artifact Tabs (With Delete Support) */}
                  {/* 找到当前选中课时所在的阶段 */}
                  {(() => {
                    const currentStage = stages.find(stage =>
                      stage.lessons?.some(lesson => lesson.id === selectedLesson.id)
                    );

                    return (
                      <ArtifactTabs
                        lesson={selectedLesson}
                        plans={plans}
                        slides={slides}
                        onEnter={handleEnterWorkshop}
                        onDeletePlan={handleDeletePlan}
                        onDeleteSlideVersion={handleDeleteSlideVersion}
                        onPlanVersionChange={handlePlanVersionChange}
                        currentUserId={user?.id || 0}
                        currentUserRole={user?.role || ''}
                        planId={selectedPlanId || 0}
                        onSlideStatusChange={handleSlideStatusChange}
                        lessonInfo={{
                          id: selectedLesson.id,
                          title: selectedLesson.title,
                          sort_order: selectedLesson.sort_order
                        }}
                        stageInfo={currentStage ? {
                          title: currentStage.title
                        } : undefined}
                        courseId={cId}
                      />
                    );
                  })()}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-400"><FileText size={48} className="mb-4 text-gray-300" /><p>Select a lesson to view details.</p></div>
              )}
            </div>
          </div>
        </>
      )}
      <StudentPortalButton />
    </div>
  );
}
